import { Static, Type, TSchema } from '@sinclair/typebox';

export const WebSocketMessageSchema = Type.Object({
  type: Type.Union([
    Type.Literal('request'), // Expects response
    Type.Literal('response'), // Response to request
    Type.Literal('broadcast'), // One-way message (legacy 'message')
    Type.Literal('channel_message'), // One-way message (legacy 'message')
  ]),
  channelId: Type.String(), // module name
  payload: Type.Unknown(), // module payload
  messageId: Type.String(), // unique message identifier
  replyTo: Type.Optional(Type.String()), // For responses - references original messageId
  error: Type.Optional(Type.String()), // For error responses
});

export type WebSocketMessage = Static<typeof WebSocketMessageSchema>;

/**
 * Ожидаемый запрос (клиент): хранит обработчики промиса и метаданные.
 * Значение ответа помечено как unknown до уровня канала, где выполняется валидация.
 */
export interface PendingRequest {
  resolve: (value: unknown) => void;
  reject: (error: Error) => void;
  timeout: NodeJS.Timeout;
  channelId: string;
  timestamp: number;
}

export interface RequestOptions {
  timeout?: number; // milliseconds, default 5000
}

export interface QueuedMessage {
  id: string;
  data: WebSocketMessage;
  timestamp: number;
  attempts: number;
  createdAt: string; // ISO string for debug
}

export enum ConnectionState {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  RECONNECTING = 'reconnecting',
}

export interface ConnectionStatusInfo {
  state: ConnectionState;
  reconnectAttempts: number;
  queuedMessages: number;
  lastConnected?: string;
  lastError?: string;
}

export class ValidationError extends Error {
  constructor(
    message: string,
    public errors: unknown[],
    public data: unknown
  ) {
    super(message);
    this.name = 'ValidationError';
  }
}

export interface ValidationResult<T> {
  success: boolean;
  data: T | null;
  errors: unknown[];
}

/**
 * Функция ответа на запрос со строгой типизацией полезной нагрузки.
 */
export interface ReplyFunction<TRes> {
  (data: TRes): void;
  error(message: string): void;
}

/**
 * Канал обмена сообщениями (клиентская сторона) с строгой типизацией.
 *
 * TReq — тип исходящих данных (broadcast/request),
 * TRes — тип входящих данных (channel_message/response). По умолчанию unknown.
 */
export interface Channel<TReq, TRes = unknown> {
  /** Отправить однонаправленное сообщение (broadcast) */
  send(data: TReq): void;
  /** Отправить запрос и получить ответ */
  request(data: TReq, options?: RequestOptions): Promise<TRes>;
  /** Подписаться на обычные входящие сообщения. Возвращает функцию отписки. */
  onMessage(handler: (message: TRes) => void): () => void;
  /** Обработать запрос от сервера и ответить типизированными данными. Возвращает функцию отписки. */
  onRequest(handler: (message: TReq, reply: ReplyFunction<TRes>) => void): () => void;
  /** Коллбек при потере соединения. Возвращает функцию отписки. */
  onConnectionLost(handler: () => void): () => void;
  /** Коллбек при установке соединения. Возвращает функцию отписки. */
  onConnectionStart(handler: () => void): () => void;
  /** Коллбек при ошибке подключения. Возвращает функцию отписки. */
  onError(handler: (error: Error) => void): () => void;
  /** Текущее состояние соединения */
  isConnected(): boolean;
  /**
   * Включить проверку схемами для запроса и (опционально) ответа.
   * Если схема ответа не указана, используется схема запроса.
   */
  withSchema<SReq extends TSchema, SRes extends TSchema = SReq>(
    schemaReq: SReq,
    schemaRes?: SRes
  ): Channel<Static<SReq>, Static<SRes>>;
}

// Core messaging system types (moved from schemas/types.ts)
export const MessagePayload = Type.Object({
  userId: Type.String({ description: 'Target user ID' }),
  payload: Type.Unknown({ description: 'Message payload - any type' }),
  timestamp: Type.Optional(Type.String({ format: 'date-time' })),
  messageId: Type.Optional(Type.String()),
});

export const ChannelMessage = Type.Object({
  channelId: Type.String({ description: 'Channel identifier (module name)' }),
  userId: Type.String({ description: 'Target user ID' }),
  payload: Type.Unknown({ description: 'Message payload' }),
  messageId: Type.String({ description: 'Unique message ID' }),
  timestamp: Type.String({ format: 'date-time' }),
});

export const ChannelConfig = Type.Object({
  channelId: Type.String({ description: 'Channel identifier' }),
  moduleName: Type.String({ description: 'Module that owns this channel' }),
  persistent: Type.Optional(Type.Boolean({ default: false })),
  retryAttempts: Type.Optional(Type.Number({ default: 3 })),
});

// Export TypeScript types
export type MessagePayload = Static<typeof MessagePayload>;
export type ChannelMessage = Static<typeof ChannelMessage>;
export type ChannelConfig = Static<typeof ChannelConfig>;
export type MessageHandler = (message: ChannelMessage) => void;
